import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translate, LOCALES } from "@/data/i18n";

const LocaleContext = createContext(null);
const STORAGE_KEY = "sf_locale";

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    if (typeof window === "undefined") return "id";
    const stored = localStorage.getItem(STORAGE_KEY);
    return LOCALES.includes(stored) ? stored : "id";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (LOCALES.includes(next)) setLocaleState(next);
  }, []);

  const toggle = useCallback(() => {
    setLocaleState((l) => (l === "id" ? "en" : "id"));
  }, []);

  const t = useCallback((key) => translate(key, locale), [locale]);

  // Helper to pick from bilingual objects like { id: "...", en: "..." }
  const pick = useCallback((obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    return obj[locale] ?? obj.id ?? obj.en ?? "";
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, toggle, t, pick }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be inside LocaleProvider");
  return ctx;
}
